import { Checkbox } from '@mui/material';
import {
  AccountActions,
  PayableActions,
  SettingsActions,
} from '@portal/actions';
import { getRouteStackPath } from '@portal/config/routes';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import { Col, Divider, Row, Tabs, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import './AccountPayable.less';

import {
  CloudUploadOutlined,
  DislikeFilled,
  DislikeOutlined,
  DownloadOutlined,
  EyeOutlined,
  LikeFilled,
  LikeOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  GridActionsColDef,
  GridCellParams,
  GridColDef,
} from '@mui/x-data-grid';
import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedDropdown from '@portal/components/AdvancedDropdown/AdvancedDropdown';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import DataTable from '@portal/components/DataTable/DataTable';
import MaintenanceModal from '@portal/components/MaintenanceModal/MaintenanceModal';
import MessageModal from '@portal/components/MessageModal/MessageModal';
import PayableButtons from '@portal/components/PayableButtons/PayableButtons';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import RolesType from '@portal/enum/rolesType';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import {
  AccountSettingKeyEnum,
  AppSettingsKeyEnum,
  DownloadAccountPayableType,
  FileTypePayable,
  PayableGroupStatus,
  PayableVoteAction,
  PaymentMode,
} from '@portal/models/enumerators';
import {
  CnabOriginProject,
  PaymentAccountType,
} from '@portal/models/enumerators/paymentAccount';
import { Payable, PayableGroup } from '@portal/models/module';
import { translate } from '@portal/services/i18n';
import { maskCpfOrCnpj } from '@portal/services/masks';
import { getEnumDescription } from '@portal/utils/enum';
import { moneyFormat } from '@portal/utils/format';
import { hasAccess } from '@portal/utils/hasAccess';
import truncateTextWithEllipsis from '@portal/utils/truncateTextWithEllipsis';
import moment from 'moment';
import { Link } from 'react-router-dom';

const initialValues: advancedFilterModels.baseFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'asc',
};

enum TabIndexEnum {
  WAITING_APPROVAL = '1',
  APPROVED = '2',
  REPROVED = '3',
  SCHEDULED = '4',
}

type PayableItem = {
  amount?: number;
};

type PayableRow = {
  id: string | number;
  totalAmount?: number;
  amount?: number;
  payable?: PayableItem[];
};

type PayableApprovalRow = {
  id: string | number;
  originProject: CnabOriginProject;
  status: PayableGroupStatus;
  userVotes?: models.PayableVotes[];
};

const AccountPayable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const [tabKey, setTabKey] = useState<TabIndexEnum>(
    TabIndexEnum.WAITING_APPROVAL
  );
  const [advancedFilters, setAdvancedFilters] = useState(initialValues);
  const [approvalModal, setApprovalModal] = useState<{
    visible: boolean;
    id: string | null;
    action: PayableVoteAction | null;
  }>({
    visible: false,
    id: null,
    action: null,
  });
  const [pinModal, setPinModal] = useState(false);
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<
    models.PayableGroup[]
  >([]);
  const [batchApprovalModal, setBatchApprovalModal] = useState<{
    visible: boolean;
    action: PayableVoteAction | null;
    selectedIds: string[];
  }>({
    visible: false,
    action: null,
    selectedIds: [],
  });

  const {
    user: { roles, me },
    payable: { payableGroupsReport, countPayableGroup, payablePreview },
    account: { config },
    paymentAccount: { selectedPaymentAccount },
    settings: { remoteConfig, keys },
  } = useReduxState();

  useEffect(() => {
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }
  }, [dispatch, remoteConfig]);

  const getPaymentRows = (
    filters: advancedFilterModels.baseFilter,
    statusFilter?: PayableGroupStatus[]
  ) => {
    const filtersCopy = {
      ...advancedFilters,
      ...filters,
      ...(statusFilter && { statusMany: statusFilter }),
    };
    setAdvancedFilters(filtersCopy);

    dispatch(
      PayableActions.getPayableGroups(
        selectedPaymentAccount !== null
          ? selectedPaymentAccount.id
          : me.account.paymentAccounts.find(
              (account) => account.type === PaymentAccountType.ACCOUNT
            )?.id ?? '',
        filtersCopy
      )
    );
  };

  const getStatusFilterForTab = (
    tabKey: TabIndexEnum
  ): PayableGroupStatus[] => {
    switch (tabKey) {
      case TabIndexEnum.WAITING_APPROVAL:
        return [PayableGroupStatus.WAITING_APPROVAL];

      case TabIndexEnum.APPROVED:
        return [
          PayableGroupStatus.EXECUTED,
          PayableGroupStatus.PORTABILITY_EXECUTED,
          PayableGroupStatus.APPROVED_WITH_ERROR,
        ];

      case TabIndexEnum.REPROVED:
        return [PayableGroupStatus.REPROVED];

      default:
        return [PayableGroupStatus.SCHEDULED];
    }
  };

  const handleTabChange = (key: TabIndexEnum) => {
    setTabKey(key);
    setSelectedRows([]);
    setSelectedRowsData([]);
    const statusFilter = getStatusFilterForTab(key);
    getPaymentRows(initialValues, statusFilter);
  };

  const getApprovalPreview = (
    action: PayableVoteAction,
    bucket?: string | null,
    filename?: string | null,
    id?: string | null,
    fileType?: number
  ) => {
    if (bucket && filename && id) {
      dispatch(
        PayableActions.getPayablesPreview(
          bucket,
          filename,
          selectedPaymentAccount !== null
            ? selectedPaymentAccount.id
            : me.account.paymentAccounts.find(
                (account) => account.type === PaymentAccountType.ACCOUNT
              )?.id ?? '',
          fileType === FileTypePayable.CNAB
            ? FileTypePayable.CNAB
            : FileTypePayable.CSV
        )
      );
      setApprovalModal((prev) => ({ ...prev, id, action }));
    }
  };

  const confirmApprove = async (pin: string) => {
    if (approvalModal.id && pin) {
      const paymentAccountId =
        selectedPaymentAccount !== null
          ? selectedPaymentAccount.id
          : me.account.paymentAccounts.find(
              (account) => account.type === PaymentAccountType.ACCOUNT
            )?.id ?? '';

      const encryptedPin = await encrypt(pin);

      if (approvalModal.action === PayableVoteAction.APPROVE) {
        dispatch(
          PayableActions.approvePayableGroup(
            approvalModal.id,
            encryptedPin,
            paymentAccountId,
            advancedFilters
          )
        );
      } else {
        dispatch(
          PayableActions.reprovePayableGroup(
            approvalModal.id,
            encryptedPin,
            paymentAccountId,
            advancedFilters
          )
        );
      }
    }
    cleanApprovalPreview();
  };

  const cleanApprovalPreview = () => {
    dispatch(PayableActions.cleanPreview());
    setApprovalModal({
      visible: false,
      id: null,
      action: null,
    });
    setPinModal(false);
  };

  const handleRowSelection = (rowId: string, checked: boolean) => {
    if (checked) {
      setSelectedRows([...selectedRows, rowId]);
      // Adicionar os dados completos do item selecionado
      const selectedItem = (payableGroupsReport ?? []).find(
        (row) => row.id === rowId
      );
      if (selectedItem) {
        setSelectedRowsData([...selectedRowsData, selectedItem]);
      }
    } else {
      setSelectedRows(selectedRows.filter((id) => id !== rowId));
      setSelectedRowsData(selectedRowsData.filter((item) => item.id !== rowId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const eligibleRows = (payableGroupsReport ?? []).filter(
        (row) =>
          row.status === PayableGroupStatus.WAITING_APPROVAL &&
          row.originProject === CnabOriginProject.APP &&
          !row.userVotes?.find(
            (vote: models.PayableVotes) => vote.userId === me.id
          )
      );

      const eligibleRowIds = eligibleRows.map((row) => row.id);
      setSelectedRows([...new Set([...selectedRows, ...eligibleRowIds])]);

      const newSelectedData = eligibleRows.filter(
        (row) => !selectedRowsData.some((item) => item.id === row.id)
      );
      setSelectedRowsData([...selectedRowsData, ...newSelectedData]);
    } else {
      const eligibleRowIds = (payableGroupsReport ?? [])
        .filter(
          (row) =>
            row.status === PayableGroupStatus.WAITING_APPROVAL &&
            row.originProject === CnabOriginProject.APP &&
            !row.userVotes?.find(
              (vote: models.PayableVotes) => vote.userId === me.id
            )
        )
        .map((row) => row.id);

      setSelectedRows(
        selectedRows.filter((id) => !eligibleRowIds.includes(id))
      );
      setSelectedRowsData(
        selectedRowsData.filter((item) => !eligibleRowIds.includes(item.id))
      );
    }
  };

  const handleBatchApproval = (action: PayableVoteAction) => {
    if (selectedRows.length === 0) return;

    setBatchApprovalModal({
      visible: true,
      action,
      selectedIds: selectedRows,
    });
  };

  const confirmBatchApproval = async (pin: string) => {
    if (batchApprovalModal.selectedIds.length === 0 || !pin) return;

    const paymentAccountId =
      selectedPaymentAccount !== null
        ? selectedPaymentAccount.id
        : me.account.paymentAccounts.find(
            (account) => account.type === PaymentAccountType.ACCOUNT
          )?.id ?? '';

    const encryptedPin = await encrypt(pin);

    if (batchApprovalModal.action === PayableVoteAction.APPROVE) {
      dispatch(
        PayableActions.bulkApprovePayableGroups(
          batchApprovalModal.selectedIds,
          encryptedPin,
          paymentAccountId,
          advancedFilters
        )
      );
    } else {
      dispatch(
        PayableActions.bulkReprovePayableGroups(
          batchApprovalModal.selectedIds,
          encryptedPin,
          paymentAccountId,
          advancedFilters
        )
      );
    }

    cleanBatchApproval();
  };

  const cleanBatchApproval = () => {
    setBatchApprovalModal({
      visible: false,
      action: null,
      selectedIds: [],
    });
    setSelectedRows([]);
    setSelectedRowsData([]);
  };

  const calculateSelectedRowsTotal = () => {
    // Usar os dados armazenados em vez de filtrar pela página atual
    return selectedRowsData.reduce((total, row) => {
      const rowTotal =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (row as any).totalAmount ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (row as any).amount ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (row as any).payable?.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (sum: number, payableItem: any) => sum + (payableItem.amount || 0),
          0
        ) ||
        0;
      return total + rowTotal;
    }, 0);
  };

  const downloadReturnFile = (fileId: string) => {
    dispatch(PayableActions.getExportedResponseCnab(fileId ?? ''));
  };

  const downloadModel = (type) => {
    const link = keys?.find((key) => key && Object.hasOwn(key, type));
    if (link) {
      window.open(link[type], '_blank');
    }
  };

  const downloadCSVModel = () => {
    downloadModel(DownloadAccountPayableType.CSV);
  };

  const downloadCNABModel = () => {
    downloadModel(DownloadAccountPayableType.CNAB);
  };

  useEffect(() => {
    if (!approvalModal) {
      cleanApprovalPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalModal]);

  useEffect(() => {
    if (payablePreview) {
      setApprovalModal((prev) => ({ ...prev, visible: true }));
    }
  }, [payablePreview]);

  // Sincronizar dados selecionados quando há mudança nos dados da página
  useEffect(() => {
    if (payableGroupsReport && selectedRows.length > 0) {
      // Atualizar os dados dos itens que estão na página atual
      const currentPageItems = payableGroupsReport.filter((row) =>
        selectedRows.includes(row.id)
      );

      // Manter os dados dos itens de outras páginas e atualizar os da página atual
      setSelectedRowsData((prevSelectedRowsData) => {
        const otherPagesData = prevSelectedRowsData.filter(
          (item) => !payableGroupsReport.some((row) => row.id === item.id)
        );

        return [...otherPagesData, ...currentPageItems];
      });
    }
  }, [payableGroupsReport, selectedRows]);

  useEffect(() => {
    const isFromRedirect = sessionStorage.getItem('payable_redirect');
    const statusFilter = getStatusFilterForTab(tabKey);

    if (isFromRedirect) {
      sessionStorage.removeItem('payable_redirect');

      const filtersCopy = { ...advancedFilters };
      setAdvancedFilters(filtersCopy);

      getPaymentRows(filtersCopy, statusFilter);
    } else {
      getPaymentRows(initialValues, statusFilter);
    }

    if (me.id) {
      dispatch(
        AccountActions.getUserConfig(
          selectedPaymentAccount !== null
            ? selectedPaymentAccount.id
            : me.account.paymentAccounts.find(
                (account) => account.type === PaymentAccountType.ACCOUNT
              )?.id ?? ''
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, me.id, selectedPaymentAccount?.id]);

  const openMaintenance = () => {
    setOpenMaintenanceModal(true);

    return;
  };

  const renderBatchActions = ({
    selectedRows,
    translate,
    moneyFormat,
    calculateSelectedRowsTotal,
    handleBatchApproval,
    setSelectedRows,
  }: {
    selectedRows: string[];
    translate: (key: string) => string;
    moneyFormat: (value: number) => string;
    calculateSelectedRowsTotal: () => number;
    handleBatchApproval: (action: PayableVoteAction) => void;
    setSelectedRows: (rows: string[]) => void;
  }) => {
    const count = selectedRows.length;
    const hasSelection = count > 0;
    const text = (key: string) =>
      translate(`PAGES.PAYMENT.ACCOUNT_PAYABLE.BATCH.${key}`);

    return (
      <AccessListControl accessRole={RolesType.PAYABLE_APPROVER}>
        <div
          className={`account-payable__batch-actions ${
            hasSelection ? '--visible' : '--hidden'
          }`}
        >
          {hasSelection ? (
            <>
              <div>
                <ThemedText className="account-payable__batch-actions__counter">
                  {count}{' '}
                  {text(count === 1 ? 'ITEM_SELECTED' : 'ITEMS_SELECTED')}
                </ThemedText>
                <ThemedText className="account-payable__batch-actions__total">
                  {text('TOTAL_LABEL')}{' '}
                  <strong>{moneyFormat(calculateSelectedRowsTotal())}</strong>
                </ThemedText>
              </div>
              <div className="account-payable__batch-actions__buttons">
                {(
                  [
                    {
                      text: text('APPROVE_SELECTED'),
                      color: 'primary' as const,
                      icon: <LikeFilled />,
                      action: PayableVoteAction.APPROVE,
                    },
                    {
                      text: text('REPROVE_SELECTED'),
                      color: 'secondary' as const,
                      icon: <DislikeFilled />,
                      action: PayableVoteAction.REPROVE,
                    },
                  ] as {
                    text: string;
                    color: 'primary' | 'secondary' | 'default' | 'inherit';
                    icon: React.ReactNode;
                    action: PayableVoteAction;
                  }[]
                ).map(({ text, color, icon, action }) => (
                  <AdvancedButton
                    key={text}
                    text={text}
                    variant="contained"
                    color={color}
                    size="small"
                    endIcon={icon}
                    onClick={() => handleBatchApproval(action)}
                    useSystemColor={false}
                  />
                ))}
                <AdvancedButton
                  text={text('CLEAR_SELECTION')}
                  variant="outlined"
                  color="default"
                  size="small"
                  onClick={() => setSelectedRows([])}
                />
              </div>
            </>
          ) : null}
        </div>
      </AccessListControl>
    );
  };

  const getDataTableColumns = (): (GridColDef | GridActionsColDef)[] => [
    {
      field: 'id',
      flex: 1,
      headerName: 'id',
      sortable: false,
      hide: true,
    },
    {
      field: 'fileName',
      flex: 1,
      headerName: translate('PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.FILE_NAME'),
      renderCell: (column: GridCellParams) => {
        let text = column.row.referenceName;

        if (!column.row?.referenceName) {
          text = translate('APPLICATION.NO_INFORMATION');
        }

        return (
          <Tooltip title={text} placement="top">
            {truncateTextWithEllipsis(text, 20)}
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      flex: 1,
      headerName: translate('PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.STATUS'),
      renderCell: (column: GridCellParams) =>
        column.row.status && (
          <Tooltip
            title={getEnumDescription(
              column.row.status,
              'PAYABLE_GROUP_STATUS'
            )}
            placement="top"
          >
            {truncateTextWithEllipsis(
              getEnumDescription(
                column.row.status,
                'PAYABLE_GROUP_STATUS'
              ) as string,
              25
            )}
          </Tooltip>
        ),
    },
    {
      field: 'totalAmount',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      headerName: translate('PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.AMOUNT'),
      renderCell: (column: GridCellParams) => {
        const totalAmount =
          column.row.totalAmount ??
          column.row.amount ??
          column.row.payable?.reduce(
            (sum: number, p: Payable) => sum + (p.amount || 0),
            0
          ) ??
          0;

        return (
          <Tooltip title={moneyFormat(totalAmount)} placement="top">
            <div>{moneyFormat(totalAmount)}</div>
          </Tooltip>
        );
      },
    },
    {
      headerName: translate(
        'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.REFERENCE_DATE'
      ),
      field: 'referenceDate',
      flex: 1,
      renderCell: (column: GridCellParams) => {
        const formattedDate = moment(column.row.referenceDate).format(
          'DD/MM/YYYY'
        );
        return column.row.referenceDate ? (
          <Tooltip title={formattedDate} placement="top">
            <div>{formattedDate}</div>
          </Tooltip>
        ) : (
          <Divider
            type="horizontal"
            className="account-payable__container__divider__inner"
          />
        );
      },
    },
    {
      field: 'action',
      align: 'center',
      type: 'actions',
      headerAlign: 'center',
      headerName: translate('PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.ACTIONS'),
      flex: 1,
      renderCell: (column: GridCellParams) => (
        <div className="account-payable__details">
          <AdvancedDropdown
            icon={<SettingOutlined />}
            placement="top"
            testId="account-payable__details--button"
            items={[
              {
                key: 'details',
                icon: <EyeOutlined />,
                label: (
                  <Link
                    to={{
                      pathname: `/arquivos/detalhes-arquivos/${column.row.id}`,
                      search: `?mode=${PaymentMode.PAYABLE}`,
                    }}
                  >
                    <button
                      className="transparent-button users__details--button"
                      type="button"
                    >
                      {translate(
                        'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.DETAILS'
                      )}
                    </button>
                  </Link>
                ),
                disabled: !hasAccess(roles, RolesType.PAYABLE_DETAILS),
              },
              {
                key: 'sent-file',
                icon: <CloudUploadOutlined />,
                disabled: !column.row.bucketUrl,
                label: (
                  <button
                    className="transparent-button"
                    type="button"
                    disabled={!column.row.bucketUrl}
                    onClick={() => window.location.assign(column.row.bucketUrl)}
                  >
                    {translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.SENT_FILE'
                    )}
                  </button>
                ),
              },
              {
                key: 'return-file',
                icon: <DownloadOutlined />,
                disabled: ![
                  PayableGroupStatus.EXECUTED,
                  PayableGroupStatus.PORTABILITY_EXECUTED,
                  PayableGroupStatus.APPROVED_WITH_ERROR,
                ].includes(column.row.status),
                label: (
                  <button
                    className="transparent-button"
                    type="button"
                    disabled={
                      ![
                        PayableGroupStatus.EXECUTED,
                        PayableGroupStatus.PORTABILITY_EXECUTED,
                        PayableGroupStatus.APPROVED_WITH_ERROR,
                      ].includes(column.row.status)
                    }
                    onClick={() => downloadReturnFile(column.row.id)}
                  >
                    {translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.RETURN_FILE'
                    )}
                  </button>
                ),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const renderDataTable = (rows: PayableGroup[]) => (
    <DataTable
      columns={getDataTableColumns()}
      rows={rows ?? []}
      page={advancedFilters.page}
      pageSize={advancedFilters.pageSize}
      onChange={(filters) => {
        const searchFilters = { ...advancedFilters, ...filters };
        setAdvancedFilters(searchFilters);
        const statusFilter = getStatusFilterForTab(tabKey);
        getPaymentRows(searchFilters, statusFilter);
      }}
      rowCount={countPayableGroup}
      orderBy={advancedFilters.orderBy}
      sort={advancedFilters.sort}
    />
  );

  const tabOptions = [
    {
      label: translate('PAGES.PAYMENT.ACCOUNT_PAYABLE.TABS.PENDING_APPROVAL'),
      key: TabIndexEnum.WAITING_APPROVAL,
      children: (
        <>
          {renderBatchActions({
            selectedRows,
            translate,
            moneyFormat,
            calculateSelectedRowsTotal,
            handleBatchApproval,
            setSelectedRows,
          })}
          <DataTable
            columns={[
              {
                field: 'id',
                flex: 1,
                headerName: 'id',
                sortable: false,
                hide: true,
              },
              {
                field: 'select',
                width: 50,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                headerName: '',
                renderHeader: () => {
                  const eligibleRows = (payableGroupsReport ?? []).filter(
                    (row) =>
                      row.status === PayableGroupStatus.WAITING_APPROVAL &&
                      row.originProject === CnabOriginProject.APP &&
                      !row.userVotes?.some(
                        (vote: models.PayableVotes) => vote.userId === me.id
                      )
                  );

                  const selectedCount = selectedRows.length;
                  const totalEligible = eligibleRows.length;

                  return (
                    <AccessListControl accessRole={RolesType.PAYABLE_APPROVER}>
                      <Checkbox
                        checked={
                          selectedCount > 0 && selectedCount === totalEligible
                        }
                        indeterminate={
                          selectedCount > 0 && selectedCount < totalEligible
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </AccessListControl>
                  );
                },
                renderCell: ({ row }) => {
                  const isEligible =
                    row.status === PayableGroupStatus.WAITING_APPROVAL &&
                    row.originProject === CnabOriginProject.APP &&
                    !row.userVotes?.some(
                      (vote: models.PayableVotes) => vote.userId === me.id
                    );

                  return (
                    <AccessListControl accessRole={RolesType.PAYABLE_APPROVER}>
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        disabled={!isEligible}
                        onChange={(e) =>
                          handleRowSelection(row.id, e.target.checked)
                        }
                      />
                    </AccessListControl>
                  );
                },
              },
              {
                field: 'fileName',
                flex: 1,
                headerName: translate(
                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.FILE_NAME'
                ),
                renderCell: (column) => {
                  let text = column.row.referenceName;

                  if (!column.row?.referenceName) {
                    text = translate('APPLICATION.NO_INFORMATION');
                  }

                  return (
                    <Tooltip title={text} placement="top">
                      {truncateTextWithEllipsis(text, 15)}
                    </Tooltip>
                  );
                },
              },
              {
                field: 'status',
                flex: 1,
                headerName: translate(
                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.STATUS'
                ),
                renderCell: (column: GridCellParams) =>
                  column.row.status && (
                    <Tooltip
                      title={`${getEnumDescription(
                        column.row.status,
                        'PAYABLE_GROUP_STATUS'
                      )}${
                        column.row.payable?.[0]?.message
                          ? ' - ' + column.row.payable[0].message
                          : ''
                      }`}
                      placement="top"
                    >
                      {truncateTextWithEllipsis(
                        getEnumDescription(
                          column.row.status,
                          'PAYABLE_GROUP_STATUS'
                        ) as string,
                        15,
                        true
                      )}
                    </Tooltip>
                  ),
              },
              {
                field: 'totalAmount',
                flex: 1,
                headerAlign: 'right',
                align: 'right',
                headerName: translate(
                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.AMOUNT'
                ),
                renderCell: ({ row }) => {
                  const getTotalAmount = (row: PayableRow): number => {
                    if (typeof row.totalAmount === 'number')
                      return row.totalAmount;
                    if (typeof row.amount === 'number') return row.amount;
                    if (Array.isArray(row.payable))
                      return row.payable.reduce(
                        (sum, item) => sum + (item.amount ?? 0),
                        0
                      );

                    return 0;
                  };

                  const total = getTotalAmount(row);
                  const formatted = moneyFormat(total);

                  return (
                    <Tooltip title={formatted} placement="top">
                      <div>{formatted}</div>
                    </Tooltip>
                  );
                },
              },
              {
                field: 'approval',
                flex: 1,
                headerAlign: 'center',
                align: 'center',
                headerName: translate(
                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.APPROVAL'
                ),
                renderCell: ({ row }: { row: PayableApprovalRow }) => {
                  const hasVotesFromApp =
                    !!row.userVotes &&
                    row.originProject === CnabOriginProject.APP;
                  const isWaitingApproval =
                    row.status === PayableGroupStatus.WAITING_APPROVAL;

                  if (!hasVotesFromApp) {
                    return (
                      <div className="account-payable__container__divider">
                        <Divider
                          type="horizontal"
                          className="account-payable__container__divider__inner"
                        />
                      </div>
                    );
                  }

                  const approvalCount =
                    row.userVotes?.filter(
                      (v) => v.action === PayableVoteAction.APPROVE
                    ).length ?? 0;

                  const showCount = [
                    PayableGroupStatus.EXECUTED,
                    PayableGroupStatus.PORTABILITY_EXECUTED,
                    PayableGroupStatus.SCHEDULED,
                    PayableGroupStatus.APPROVED_WITH_ERROR,
                  ].includes(row.status);

                  const requiredApprovals = showCount
                    ? approvalCount
                    : me?.account?.settings?.find(
                        (setting) =>
                          setting.key ===
                          AccountSettingKeyEnum.CNAB_REQUIRED_APPROVALS
                      )?.value ??
                      config.find(
                        (conf) =>
                          conf.key === AppSettingsKeyEnum.REQUIRED_APPROVALS
                      )?.value ??
                      '1';

                  const tooltip = `${translate(
                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.APPROVAL'
                  )}: ${approvalCount} / ${requiredApprovals} ${translate(
                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.REQUIRED'
                  )}`;

                  const ApprovalDisplay = (
                    <Tooltip title={tooltip} placement="top">
                      <div className="approval-status">
                        <span className="approval-count">{approvalCount}</span>
                        <span className="approval-separator">/</span>
                        <span className="approval-required">
                          {requiredApprovals}
                        </span>
                      </div>
                    </Tooltip>
                  );

                  return isWaitingApproval ? (
                    <AccessListControl accessRole={RolesType.PAYABLE_APPROVER}>
                      {ApprovalDisplay}
                    </AccessListControl>
                  ) : (
                    ApprovalDisplay
                  );
                },
              },
              {
                headerName: translate(
                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.REFERENCE_DATE'
                ),
                field: 'referenceDate',
                flex: 1,
                renderCell: (column) => {
                  const formattedDate = moment(column.row.referenceDate).format(
                    'DD/MM/YYYY'
                  );

                  return (
                    <>
                      {column.row.referenceDate ? (
                        <Tooltip title={formattedDate} placement="top">
                          <div>{formattedDate}</div>
                        </Tooltip>
                      ) : (
                        <Divider
                          type="horizontal"
                          className="account-payable__container__divider__inner"
                        />
                      )}
                    </>
                  );
                },
              },
              {
                field: 'action',
                align: 'center',
                headerAlign: 'center',
                headerName: translate(
                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.ACTIONS'
                ),
                flex: 1,
                renderCell: (column) => (
                  <div className="account-payable__details">
                    <AdvancedDropdown
                      testId="account-payable-dropdown"
                      icon={<SettingOutlined />}
                      placement="topRight"
                      items={[
                        {
                          key: 'details',
                          icon: <EyeOutlined />,
                          label: (
                            <Link
                              to={{
                                pathname: `/arquivos/detalhes-arquivos/${column.row.id}`,
                                search: `?mode=${PaymentMode.PAYABLE}`,
                              }}
                            >
                              <button
                                className="transparent-button users__details--button"
                                type="button"
                                disabled={
                                  !hasAccess(roles, RolesType.PAYABLE_DETAILS)
                                }
                              >
                                {translate(
                                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.DETAILS'
                                )}
                              </button>
                            </Link>
                          ),
                        },
                        {
                          key: 'approve',
                          icon: <LikeOutlined />,
                          disabled:
                            column.row.status !==
                              PayableGroupStatus.WAITING_APPROVAL ||
                            column.row.originProject !==
                              CnabOriginProject.APP ||
                            column.row.userVotes?.find(
                              (vote: models.PayableVotes) =>
                                vote.userId === me.id
                            )?.action === PayableVoteAction.APPROVE,
                          label: (
                            <AccessListControl
                              accessRole={RolesType.PAYABLE_APPROVER}
                              NoAccessComponent={<></>}
                            >
                              <button
                                className={`transparent-button approval-btn ${
                                  column.row.userVotes?.find(
                                    (vote: models.PayableVotes) =>
                                      vote.userId === me.id
                                  )?.action === PayableVoteAction.APPROVE
                                    ? 'approved'
                                    : ''
                                }`}
                                disabled={
                                  column.row.status !==
                                    PayableGroupStatus.WAITING_APPROVAL ||
                                  column.row.originProject !==
                                    CnabOriginProject.APP ||
                                  column.row.userVotes?.find(
                                    (vote: models.PayableVotes) =>
                                      vote.userId === me.id
                                  )?.action === PayableVoteAction.APPROVE
                                }
                                onClick={() =>
                                  getApprovalPreview(
                                    PayableVoteAction.APPROVE,
                                    column.row.bucketRemittance,
                                    column.row.referenceName,
                                    column.row.id,
                                    column.row.fileType
                                  )
                                }
                              >
                                {translate(
                                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.APPROVE'
                                )}
                              </button>
                            </AccessListControl>
                          ),
                        },
                        {
                          key: 'reprove',
                          icon: <DislikeOutlined />,
                          disabled:
                            column.row.status !==
                              PayableGroupStatus.WAITING_APPROVAL ||
                            column.row.originProject !==
                              CnabOriginProject.APP ||
                            column.row.userVotes?.find(
                              (vote: models.PayableVotes) =>
                                vote.userId === me.id
                            )?.action === PayableVoteAction.REPROVE,
                          label: (
                            <AccessListControl
                              accessRole={RolesType.PAYABLE_APPROVER}
                              NoAccessComponent={<></>}
                            >
                              <button
                                className={`transparent-button approval-btn ${
                                  column.row.userVotes?.find(
                                    (vote: models.PayableVotes) =>
                                      vote.userId === me.id
                                  )?.action === PayableVoteAction.REPROVE
                                    ? 'reproved'
                                    : ''
                                }`}
                                disabled={
                                  column.row.status !==
                                    PayableGroupStatus.WAITING_APPROVAL ||
                                  column.row.originProject !==
                                    CnabOriginProject.APP ||
                                  column.row.userVotes?.find(
                                    (vote: models.PayableVotes) =>
                                      vote.userId === me.id
                                  )?.action === PayableVoteAction.REPROVE
                                }
                                onClick={() =>
                                  getApprovalPreview(
                                    PayableVoteAction.REPROVE,
                                    column.row.bucketRemittance,
                                    column.row.referenceName,
                                    column.row.id,
                                    column.row.fileType
                                  )
                                }
                              >
                                {translate(
                                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.REPROVE'
                                )}
                              </button>
                            </AccessListControl>
                          ),
                        },
                        {
                          key: 'sent-file',
                          icon: <CloudUploadOutlined />,
                          disabled: !column.row.bucketUrl,
                          label: (
                            <button
                              className="transparent-button"
                              type="button"
                              disabled={!column.row.bucketUrl}
                              onClick={() =>
                                window.location.assign(column.row.bucketUrl)
                              }
                            >
                              {translate(
                                'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.SENT_FILE'
                              )}
                            </button>
                          ),
                        },
                        {
                          key: 'return-file',
                          icon: <DownloadOutlined />,
                          disabled: ![
                            PayableGroupStatus.EXECUTED,
                            PayableGroupStatus.PORTABILITY_EXECUTED,
                            PayableGroupStatus.APPROVED_WITH_ERROR,
                          ].includes(column.row.status),
                          label: (
                            <button
                              className="transparent-button"
                              type="button"
                              disabled={
                                ![
                                  PayableGroupStatus.EXECUTED,
                                  PayableGroupStatus.PORTABILITY_EXECUTED,
                                  PayableGroupStatus.APPROVED_WITH_ERROR,
                                ].includes(column.row.status)
                              }
                              onClick={() => downloadReturnFile(column.row.id)}
                            >
                              {translate(
                                'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.RETURN_FILE'
                              )}
                            </button>
                          ),
                        },
                      ]}
                    />
                  </div>
                ),
              },
            ]}
            rows={payableGroupsReport ?? []}
            page={advancedFilters.page}
            pageSize={advancedFilters.pageSize}
            onChange={(filters) => {
              const searchFilters = {
                ...advancedFilters,
                ...filters,
              };
              setAdvancedFilters(searchFilters);
              const statusFilter = getStatusFilterForTab(tabKey);
              getPaymentRows(searchFilters, statusFilter);
            }}
            rowCount={countPayableGroup}
            orderBy={advancedFilters.orderBy}
            sort={advancedFilters.sort}
          />
        </>
      ),
    },
    {
      label: translate('PAGES.PAYMENT.ACCOUNT_PAYABLE.TABS.APPROVED'),
      key: TabIndexEnum.APPROVED,
      children: renderDataTable(payableGroupsReport),
    },
    {
      label: translate('PAGES.PAYMENT.ACCOUNT_PAYABLE.TABS.REPROVED'),
      key: TabIndexEnum.REPROVED,
      children: renderDataTable(payableGroupsReport),
    },
    {
      label: translate('PAGES.PAYMENT.ACCOUNT_PAYABLE.TABS.SCHEDULED'),
      key: TabIndexEnum.SCHEDULED,
      children: renderDataTable(payableGroupsReport),
    },
  ];

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate(`PAGES.PAYMENT.ACCOUNT_PAYABLE.TITLE`),
          },
        ]}
      />
      <Col className="account-payable">
        <AdvancedPanelContentHeader
          testId="account-payable-header"
          title={translate(`PAGES.PAYMENT.ACCOUNT_PAYABLE.TITLE`)}
          description={translate(`PAGES.PAYMENT.ACCOUNT_PAYABLE.DESCRIPTION`)}
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
          buttonTitle={translate('APPLICATION.BACK_TO_INIT')}
        />
        <AdvancedCard testId="account-payable-card">
          <div className="account-payable__button">
            {keys ? (
              <>
                <AdvancedButton
                  text={translate(
                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.LINKS.GET_MODEL_CSV'
                  )}
                  onClick={downloadCSVModel}
                  variant="outlined"
                  testId="download-csv-model-button"
                />
                <AdvancedButton
                  text={translate(
                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.LINKS.GET_MODEL_CNAB'
                  )}
                  onClick={downloadCNABModel}
                  variant="outlined"
                  testId="download-cnab-model-button"
                />
              </>
            ) : null}
            <AccessListControl accessRole={RolesType.PAYABLE_CREATE}>
              <PayableButtons openMaintenance={openMaintenance} />
            </AccessListControl>
          </div>
          <Tabs
            activeKey={tabKey}
            onChange={(value) => handleTabChange(value as TabIndexEnum)}
            type="card"
          >
            {tabOptions.map((tab) => (
              <Tabs.TabPane tab={tab.label} key={tab.key}>
                {tab.children}
              </Tabs.TabPane>
            ))}
          </Tabs>
        </AdvancedCard>
        <MessageModal
          open={approvalModal.visible}
          setVisible={(visible) =>
            setApprovalModal((prev) => ({ ...prev, visible }))
          }
          messageAlign="start"
          buttonsAlign="end"
          message={translate(
            'PAGES.PAYMENT.ACCOUNT_PAYABLE.APPROVAL_MODAL.TITLE'
          )}
          onConfirm={() => setPinModal(true)}
          onCancel={cleanApprovalPreview}
          buttonCancelText={translate('SHARED.CANCEL')}
          buttonOkText={translate(
            approvalModal.action === PayableVoteAction.APPROVE
              ? 'PAGES.PAYMENT.ACCOUNT_PAYABLE.APPROVAL_MODAL.APPROVE'
              : 'PAGES.PAYMENT.ACCOUNT_PAYABLE.APPROVAL_MODAL.REPROVE'
          )}
          disabledConfirmButton={
            payablePreview?.rows?.length === 0 ||
            payablePreview?.rows?.some(
              (row) =>
                getEnumDescription(
                  row?.type ?? '',
                  'TRANSACTION_TYPE',
                  'SHARED.NON_EXISTENT_TYPE'
                ) === translate('SHARED.NON_EXISTENT_TYPE')
            )
          }
          width={'auto'}
        >
          <>
            <Row className="account-payable__container__modal-header">
              <Col>
                <ThemedText variant="h3">
                  {translate(
                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.APPROVAL_MODAL.HEADER_LABEL.ALL'
                  )}
                </ThemedText>
                <h4>{moneyFormat(payablePreview?.total?.ALL ?? 0)}</h4>
              </Col>
              <Col>
                <ThemedText variant="h3">
                  {translate(
                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.APPROVAL_MODAL.HEADER_LABEL.P2P'
                  )}
                </ThemedText>
                <ThemedText variant="h4">
                  {moneyFormat(payablePreview?.total?.P2P ?? 0)}
                </ThemedText>
              </Col>
              <Col>
                <ThemedText variant="h3">
                  {translate(
                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.APPROVAL_MODAL.HEADER_LABEL.TED'
                  )}
                </ThemedText>
                <ThemedText variant="h4">
                  {moneyFormat(payablePreview?.total?.TED ?? 0)}
                </ThemedText>
              </Col>
              <Col>
                <ThemedText variant="h3">
                  {translate(
                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.APPROVAL_MODAL.HEADER_LABEL.PIX'
                  )}
                </ThemedText>
                <ThemedText variant="h4">
                  {moneyFormat(payablePreview?.total?.PIX ?? 0)}
                </ThemedText>
              </Col>
            </Row>

            <div className="account-payable__container__modal-table">
              <DataTable
                columns={[
                  {
                    field: 'id',
                    flex: 1,
                    headerName: 'id',
                    sortable: false,
                    hide: true,
                  },
                  {
                    field: 'payeeName',
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    headerName: translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.NAME'
                    ),
                    renderCell: (column) => (
                      <Tooltip title={column.row.payeeName} placement="top">
                        <div>{column.row.payeeName}</div>
                      </Tooltip>
                    ),
                  },
                  {
                    field: 'payeeTaxIdentifier',
                    width: 150,
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    headerName: translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.TAX_IDENTIFIER'
                    ),
                    renderCell: (column) => (
                      <Tooltip
                        title={maskCpfOrCnpj(
                          `${column.row.payeeTaxIdentifier}`
                        )}
                        placement="top"
                      >
                        <div>
                          {maskCpfOrCnpj(`${column.row.payeeTaxIdentifier}`)}
                        </div>
                      </Tooltip>
                    ),
                  },
                  {
                    field: 'payeeAgency',
                    width: 80,
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    headerName: translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.BANK_BRANCH'
                    ),
                    renderCell: (column) => (
                      <Tooltip
                        title={
                          column.row.payeeAgencyDigit
                            ? `${column.row.payeeAgency} - ${column.row.payeeAgencyDigit}`
                            : column.row.payeeAgency
                        }
                        placement="top"
                      >
                        <div>
                          {column.row.payeeAgencyDigit
                            ? `${column.row.payeeAgency} - ${column.row.payeeAgencyDigit}`
                            : column.row.payeeAgency}
                        </div>
                      </Tooltip>
                    ),
                  },
                  {
                    field: 'payeeAccount',
                    width: 120,
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    headerName: translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.BANK_ACCOUNT'
                    ),
                    renderCell: (column) => (
                      <Tooltip
                        title={
                          column.row.payeeAccountDigit
                            ? `${column.row.payeeAccount} - ${column.row.payeeAccountDigit}`
                            : column.row.payeeAccount
                        }
                        placement="top"
                      >
                        <div>
                          {column.row.payeeAccountDigit
                            ? `${column.row.payeeAccount} - ${column.row.payeeAccountDigit}`
                            : column.row.payeeAccount}
                        </div>
                      </Tooltip>
                    ),
                  },
                  {
                    field: 'type',
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    headerName: translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.TYPE'
                    ),
                    renderCell: (column) => (
                      <Tooltip
                        title={getEnumDescription(
                          column.row.type,
                          'TRANSACTION_TYPE',
                          'SHARED.NON_EXISTENT_TYPE'
                        )}
                        placement="top"
                      >
                        <div>
                          {getEnumDescription(
                            column.row.type,
                            'TRANSACTION_TYPE',
                            'SHARED.NON_EXISTENT_TYPE'
                          )}
                        </div>
                      </Tooltip>
                    ),
                  },
                  {
                    field: 'amount',
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    headerName: translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.AMOUNT'
                    ),
                    renderCell: (column) => (
                      <Tooltip
                        title={moneyFormat(column.row.amount)}
                        placement="top"
                      >
                        <div>{moneyFormat(column.row.amount)}</div>
                      </Tooltip>
                    ),
                  },
                  {
                    field: 'status',
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    headerName: translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.STATUS'
                    ),
                    renderCell: (column: GridCellParams) =>
                      column.row.status && (
                        <Tooltip
                          title={getEnumDescription(
                            column.row.status,
                            'PAYABLE_STATUS'
                          )}
                          placement="top"
                        >
                          <div>
                            {getEnumDescription(
                              column.row.status,
                              'PAYABLE_STATUS'
                            )}
                          </div>
                        </Tooltip>
                      ),
                  },
                ]}
                getRowId={(row) => row.lineId}
                rows={payablePreview?.rows ?? []}
                pageSize={advancedFilters.pageSize}
                orderBy={advancedFilters.orderBy}
                sort={advancedFilters.sort}
                paginationMode="client"
              />
            </div>
          </>
        </MessageModal>
        <ConfirmationModal
          visible={pinModal}
          setVisible={setPinModal}
          handleConfirmation={(pin: string) => confirmApprove(pin)}
        />
        <ConfirmationModal
          visible={batchApprovalModal.visible}
          setVisible={(visible) =>
            setBatchApprovalModal((prev) => ({ ...prev, visible }))
          }
          handleConfirmation={(pin: string) => confirmBatchApproval(pin)}
        />
      </Col>
      <MaintenanceModal
        setVisible={setOpenMaintenanceModal}
        visible={openMaintenanceModal}
      />
    </>
  );
};

export default AccountPayable;
